import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../common/button/commonButton';
import ReusableTable from '../../common/table/CommonTable';
import CustomTextField from '../../common/textfield/CommonTextField';
import {
  addSpecialist,
  deleteSpecialist,
  fetchSpecialists,
  updateSpecialist,
} from '../../redux/specialist/specialistAction';

const SpecializationManagementForm = () => {
  const [specializations, setSpecializations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [specializationToDelete, setSpecializationToDelete] = useState(null);
  const [dialogMode, setDialogMode] = useState('add');
  const [selectedSpecialization, setSelectedSpecialization] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm();
  const watchStatus = watch('status', true);

  const specializationList = useSelector(
    (state) => state?.specialist?.fetchSpecialists || {}
  );
  const specializationDelete = useSelector(
    (state) => state?.specialist?.deleteSpecialist || {}
  );
  const specializationAdd = useSelector(
    (state) => state?.specialist?.addSpecialist || {}
  );
  const specializationUpdate = useSelector(
    (state) => state?.specialist?.updateSpecialist || {}
  );
  useEffect(() => {
    dispatch(fetchSpecialists());
  }, [dispatch]);

  const normalizeSpecializationName = (name) => {
    if (!name) return '';
    return name.replace(/\s+/g, ' ').trim().toLowerCase();
  };

  useEffect(() => {
    if (specializationList?.specialists?.data) {
      setSpecializations(
        specializationList.specialists.data.map((specialization, index) => ({
          id: specialization.id,
          serial_no: index + 1,
          Name: specialization.Name,
          status: specialization.status,
          normalizedName: normalizeSpecializationName(specialization.Name),
        }))
      );
    }
  }, [specializationList]);

  useEffect(() => {
    if (specializationAdd?.success) {
      setSnackbarMessage('Specialization added successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      dispatch(fetchSpecialists());
    } else if (specializationAdd?.error) {
      setSnackbarMessage(
        specializationAdd.error || 'Failed to add specialization'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [specializationAdd, dispatch]);

  useEffect(() => {
    if (specializationUpdate?.success) {
      setSnackbarMessage('Specialization updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      dispatch(fetchSpecialists());
    } else if (specializationUpdate?.error) {
      setSnackbarMessage(
        specializationUpdate.error || 'Failed to update specialization'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [specializationUpdate, dispatch]);

  useEffect(() => {
    if (specializationDelete?.success) {
      setSnackbarMessage('Specialization deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      dispatch(fetchSpecialists());
    } else if (specializationDelete?.error) {
      setSnackbarMessage(
        specializationDelete.error || 'Failed to delete specialization'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [specializationDelete, dispatch]);

  useEffect(() => {
    if (openDialog && dialogMode === 'edit' && selectedSpecialization) {
      setValue('Name', selectedSpecialization.Name || '');
      setValue(
        'status',
        selectedSpecialization.status === 1 ||
          selectedSpecialization.status === true
      );
    }
  }, [openDialog, dialogMode, selectedSpecialization, setValue]);

  const specializationNameExists = (name, currentSpecializationId = null) => {
    const normalizedNewName = normalizeSpecializationName(name);

    return specializations.some((specialization) => {
      if (
        currentSpecializationId &&
        specialization.id === currentSpecializationId
      ) {
        return false;
      }

      const normalizedExistingName =
        specialization.normalizedName ||
        normalizeSpecializationName(specialization.Name);
      return normalizedNewName === normalizedExistingName;
    });
  };

  const columns = [
    { id: 'serial_no', label: 'Serial No', sortable: true },
    { id: 'Name', label: 'Specialization Name', sortable: true },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <Chip
          label={value === 1 ? 'Active' : 'Inactive'}
          color={value === 1 ? 'success' : 'error'}
          variant="filled"
          size="small"
          sx={{
            borderRadius: '16px',
            fontWeight: 'bold',
            minWidth: '80px',
          }}
        />
      ),
    },
  ];

  const getFormFields = () => {
    return [
      {
        name: 'Name',
        label: 'Specialization Name',
        rules: { required: 'Specialization Name is required' },
      },
    ];
  };

  const handleOpenDialog = (mode, specialization = null) => {
    setDialogMode(mode);
    setSelectedSpecialization(specialization);

    if (mode === 'edit' && specialization) {
      reset();
      setValue('Name', specialization.Name || '');
      setValue(
        'status',
        specialization.status === 1 || specialization.status === true
      );
    } else {
      reset({ Name: '', status: true });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const validateSpecializationName = (e) => {
    const value = e.target.value.replace(/\s+/g, ' ').trim();

    if (!value) {
      return;
    }

    const isDuplicate = specializationNameExists(
      value,
      dialogMode === 'edit' ? selectedSpecialization?.id : null
    );

    if (isDuplicate) {
      e.target.setCustomValidity('Specialization name already exists');
    } else {
      e.target.setCustomValidity('');
    }
  };

  const onSubmit = (data) => {
    setIsLoading(true);

    // Properly normalize the specialization name
    const Name = data.Name ? data.Name.replace(/\s+/g, ' ').trim() : '';

    // Check if name is empty after normalization
    if (!Name) {
      setSnackbarMessage('Specialization name cannot be empty');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setIsLoading(false);
      return;
    }

    // Check for duplicate specialization names
    const isDuplicate = specializationNameExists(
      Name,
      dialogMode === 'edit' ? selectedSpecialization.id : null
    );

    if (isDuplicate) {
      setSnackbarMessage(
        'Specialization name already exists. Please use a different name.'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setIsLoading(false);
      return;
    }

    if (dialogMode === 'add') {
      const formData = {
        Name: Name,
        status: 1,
      };
      dispatch(addSpecialist(formData));
    } else if (dialogMode === 'edit') {
      const formData = {
        Name: Name,
        id: selectedSpecialization.id,
        status: watchStatus ? 1 : 2,
      };
    
      dispatch(
        updateSpecialist({ id: selectedSpecialization.id, ...formData })
      );
    }
    setIsLoading(false);
    handleCloseDialog();
  };

  const handleOpenDeleteDialog = (specialization) => {
    setSpecializationToDelete(specialization);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSpecializationToDelete(null);
  };

  const handleDeleteSpecialization = () => {
    if (specializationToDelete) {
      dispatch(deleteSpecialist(specializationToDelete.id));
      handleCloseDeleteDialog();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Specialization Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
          sx={{
            borderRadius: '20px',
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 'bold',
            color: 'white',
            bgcolor: '#2c90f7',
          }}
        >
          Add Specialization
        </Button>
      </Box>

      <ReusableTable
        columns={columns}
        data={specializations}
        isLoading={isLoading}
        title="Specialization"
        onEditClick={(specialization) =>
          handleOpenDialog('edit', specialization)
        }
        onDeleteClick={(specialization) =>
          handleOpenDeleteDialog(specialization)
        }
        customEmptyMessage="No specializations found. Add a new specialization to get started."
        headerColor="#000000"
        rowsPerPage={5}
        withActions={true}
      />
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            bgcolor: '#129bab',
            color: 'white',
          }}
        >
          {dialogMode === 'add'
            ? 'Add New Specialization'
            : 'Edit Specialization'}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Paper elevation={0} sx={{ p: 2 }}>
              {getFormFields().map((field) => (
                <CustomTextField
                  key={field.name}
                  label={field.label}
                  value={watch(field.name, '')}
                  onChange={(e) => setValue(field.name, e.target.value)}
                  error={!!errors[field.name]}
                  helperText={errors[field.name]?.message}
                  fullWidth
                  margin="normal"
                  type={field.type || 'text'}
                  inputProps={{
                    maxLength: 50,
                    onBlur: (e) => {
                      const normalizedValue = e.target.value
                        .replace(/\s+/g, ' ')
                        .trim();
                      setValue(field.name, normalizedValue);
                      validateSpecializationName(e);
                    },
                  }}
                />
              ))}

              {dialogMode === 'edit' && (
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={watchStatus}
                        onChange={(e) => setValue('status', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Box>
              )}
            </Paper>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              bgcolor: '#129bab',
              justifyContent: 'center',
            }}
          >
            <CustomButton
              onClick={handleCloseDialog}
              type="button"
              text="Cancel"
              variant="contained"
              disableElevation
              size="small"
            />
            <CustomButton
              type="submit"
              text={dialogMode === 'add' ? 'Add' : 'Update'}
              variant="contained"
              disableElevation
              size="small"
              startIcon={<AddIcon />}
              disabled={
                specializationAdd?.inProgress ||
                specializationUpdate?.inProgress
              }
              loading={
                specializationAdd?.inProgress ||
                specializationUpdate?.inProgress
              }
            />
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{ bgcolor: '#f44336', color: 'white' }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description" sx={{ mt: 2 }}>
            Are you sure you want to delete the specialization "
            {specializationToDelete?.Name
              ? specializationToDelete.Name.replace(/\s+/g, ' ').trim()
              : ''}
            "? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <CustomButton
            onClick={handleCloseDeleteDialog}
            type="button"
            text="Cancel"
            variant="outlined"
            disableElevation
            size="small"
          />
          <CustomButton
            onClick={handleDeleteSpecialization}
            type="button"
            text="Delete"
            variant="contained"
            disableElevation
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
          />
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default SpecializationManagementForm;
